import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import BusinessIcon from '@mui/icons-material/Business';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Grid from '@mui/material/Grid';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import { css } from '@emotion/react';

import { buildPath, invariant } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import type {
  Id,
  FragmentType,
  ThirdPartyCategoryId,
  ActorCategoryId,
} from '@e-socna/data-layer';

import { useFindOneOrganizationQuery } from '../../../pages/Organization/hooks/useFindOneOrganizationQuery.hook';
import { PATH_ORGANIZATION_DEFAULT } from '../../../app/constants/paths.constant';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { LoadingCard } from '../../LoadingCard';
import { MenuItem } from '../../Menu/MenuItem';
import { useActionMenu } from '../../Menu/hooks/useActionMenu.hook';
import { Avatar } from '../../Avatar';
import { thirdPartyTooltipOf } from '../../../utils/third-party-tooltip.util';
import { useActorCategoriesLabel } from '../../FolderBusinessEntityList/hooks/useActorCategoriesLabel.hook';
import { ExpandMoreCard } from '../../ExpandMoreCard';
import type { Option } from '../../Menu/MenuItem/types/option.type';

import { ExternalReferencesSection } from '../../ExternalReferences/Section';

import { ACTOR_CARD_SIZE, ACTOR_CARD_SX } from '../constants/card.constant';
import { orderedRepresentativesOf } from '../utils/actors-order.util';

import { ActorCardCmp_Organization_Fragment } from './fragments/organization.fragment';
import { ActorCardCmp_ActorOrganization_Fragment } from './fragments/actor-organization.fragment';
import { RepresentativeCard } from './Representative';
import { ContactInfo } from './ContactInfo';
import { FastIcons } from './FastIcons';

interface Props {
  readonly actor: FragmentType<typeof ActorCardCmp_ActorOrganization_Fragment>;
  readonly actionsEnabled?: boolean;
  readonly consultThirdPartyInNewTab: boolean;
  readonly onStartRemove?: (id: Id) => void;
  readonly onStartUpdate?: (id: Id) => void;
}

const showRepresentativesLabel = 'Afficher les Représentants';
const hideRepresentativesLabel = 'Masquer les Représentants';

export const OrganizationActorCard = ({
  actor: pActor,
  actionsEnabled = false,
  consultThirdPartyInNewTab,
  onStartRemove: handleStartRemove,
  onStartUpdate: handleStartUpdate,
}: Readonly<Props>) => {
  invariant(
    !actionsEnabled || !!(handleStartRemove && handleStartUpdate),
    'onStartRemove & onStartUpdate are required when actionsEnabled is true',
  );

  const { thirdPartyCategoriesIdMap, actorCategoriesIdMap } =
    useReferencesContext();

  const [areRepresentativesVisible, setAreRepresentativesVisible] =
    useState(true);
  const showRepresentatives = useCallback(() => {
    setAreRepresentativesVisible(true);
  }, []);
  const hideRepresentatives = useCallback(() => {
    setAreRepresentativesVisible(false);
  }, []);

  const actor = getFragmentData(
    ActorCardCmp_ActorOrganization_Fragment,
    pActor,
  );

  const {
    anchorEl,
    handleCloseActionMenu,
    handleOpenActionMenu,
    isActionMenuOpen,
    options,
  } = useActionMenu({
    id: actor._id,
    onStartRemove: handleStartRemove,
    onStartUpdate: handleStartUpdate,
  });

  const getCategoriesLabel = useActorCategoriesLabel();

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const { data: rowOrganizationData, isSuccess: organizationIsSuccess } =
    useFindOneOrganizationQuery(actor.thirdPartyId);

  if (!organizationIsSuccess) {
    return (
      <Grid size={ACTOR_CARD_SIZE} sx={ACTOR_CARD_SX}>
        <LoadingCard />
      </Grid>
    );
  }

  const organization = getFragmentData(
    ActorCardCmp_Organization_Fragment,
    rowOrganizationData.findOneOrganization,
  );

  const path = buildPath(PATH_ORGANIZATION_DEFAULT, { id: organization._id });

  const representativesAction = !areRepresentativesVisible ? (
    <Tooltip title={showRepresentativesLabel}>
      <IconButton
        aria-label={showRepresentativesLabel}
        id={`${actor._id}-actions-show-representatives-button`}
        onClick={showRepresentatives}
        sx={{ mr: 1 }}
      >
        <VisibilityIcon />
      </IconButton>
    </Tooltip>
  ) : (
    <Tooltip title={hideRepresentativesLabel}>
      <IconButton
        aria-label={hideRepresentativesLabel}
        id={`${actor._id}-actions-hide-representatives-button`}
        onClick={hideRepresentatives}
        sx={{ mr: 1 }}
      >
        <VisibilityOffIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <Grid size={ACTOR_CARD_SIZE} sx={ACTOR_CARD_SX}>
        <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            action={
              actionsEnabled ? (
                <>
                  {representativesAction}
                  <Tooltip title="Actions sur l'Acteur">
                    <IconButton
                      aria-controls={
                        isActionMenuOpen
                          ? `${actor._id}-actions-menu`
                          : undefined
                      }
                      aria-expanded={isActionMenuOpen ? 'true' : undefined}
                      aria-haspopup="true"
                      aria-label="Actions sur l'acteur"
                      id={`${actor._id}-actions-menu-button`}
                      onClick={handleOpenActionMenu}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorEl}
                    id={`${actor._id}-actions-menu`}
                    onClose={handleCloseActionMenu}
                    open={isActionMenuOpen}
                    slotProps={{
                      list: {
                        'aria-labelledby': `${actor._id}-actions-menu-button`,
                      },
                    }}
                  >
                    {options.map((option: Option) => (
                      <MenuItem
                        key={option.key}
                        onCloseMenu={handleCloseActionMenu}
                        option={option}
                      />
                    ))}
                  </Menu>
                </>
              ) : (
                representativesAction
              )
            }
            avatar={
              <>
                <Avatar
                  bgColor={organization.color}
                  icon={<BusinessIcon />}
                  imageUri={organization.logoImageUri}
                  label={thirdPartyTooltipOf(
                    organization.name,
                    getCategoriesLabel(actor.categoryIds),
                  )}
                />
                <FastIcons
                  categoryIds={actor.categoryIds}
                  isInternal={organization.isInternal}
                  isVip={organization.isVip}
                  useItsEdmDomain={actor.useItsEdmDomain ?? false}
                />
              </>
            }
            css={css`
              .MuiCardHeader-action {
                display: contents;
              }
            `}
          />
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography
              component="div"
              gutterBottom
              sx={{ lineHeight: 1.2 }}
              variant="h6"
            >
              {organization.name}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 1.5 }}>
              Raison Sociale
            </Typography>
            <Typography color="text.secondary" variant="overline">
              Catégories du Tiers
            </Typography>
            <Typography gutterBottom variant="body2">
              {!organization.categoryIds?.length
                ? 'Non renseignées'
                : organization.categoryIds
                    .map(
                      (id) =>
                        thirdPartyCategoriesIdMap[id as ThirdPartyCategoryId]
                          ?.label ?? 'Inconnue',
                    )
                    .join(', ')}
            </Typography>
            <Typography color="text.secondary" variant="overline">
              Catégories de l&apos;Acteur
            </Typography>
            <Typography gutterBottom variant="body2">
              {!actor.categoryIds.length
                ? 'Non renseignées'
                : actor.categoryIds
                    .map(
                      (id) =>
                        actorCategoriesIdMap[id as ActorCategoryId]?.label ??
                        'Inconnue',
                    )
                    .join(', ')}
            </Typography>
            <Typography color="text.secondary" variant="overline">
              Activité de l&apos;Organisation
            </Typography>
            <Typography gutterBottom variant="body2">
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
              {organization.activity || 'Non renseignée'}
            </Typography>
            <ExternalReferencesSection references={actor.references} />
          </CardContent>
          <CardActions disableSpacing>
            {consultThirdPartyInNewTab ? (
              <Button
                component={MuiLink}
                endIcon={<OpenInNewIcon />}
                href={path}
                rel="noopener noreferrer"
                size="medium"
                target="_blank"
                variant="outlined"
              >
                Consulter
              </Button>
            ) : (
              <Button
                component={Link}
                size="medium"
                to={path}
                variant="outlined"
              >
                Consulter
              </Button>
            )}
            <ExpandMoreCard
              expanded={expanded}
              onExpandClick={handleExpandClick}
            />
          </CardActions>
          <ContactInfo
            contactInfo={organization.contactInfo}
            expanded={expanded}
          />
        </Card>
      </Grid>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!areRepresentativesVisible &&
        (actionsEnabled
          ? actor.representatives
          : orderedRepresentativesOf(actor.representatives)
        ).map((representative) => (
          <RepresentativeCard
            consultThirdPartyInNewTab={consultThirdPartyInNewTab}
            key={representative._id}
            membership={organization.allMemberships.find(
              (membership) => membership.personId === representative.personId,
            )}
            organizationName={organization.name}
            representative={representative}
          />
        ))}
    </>
  );
};
